import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from '@/lib/authFetch';
import { toast } from 'sonner';
import { Loader2, LayoutGrid, ArrowRight } from 'lucide-react';
import RecentAppCard from '@/components/home/RecentAppCard';
import { buildSiteFiles } from '@/lib/sitePages';

// The account's sites, shown the way the marketing home shows them.
//
// Same `RecentAppCard`, same heading, same "View all" — the point of the
// dashboard is that signing in does not feel like arriving in a different
// product, and a second hand-built grid would have drifted from the first the
// moment either changed.
//
// The handlers are wired here rather than shared, because what an action
// *means* differs by surface: the homepage opens a preview modal over itself,
// while the dashboard has a real preview route to send you to.

export default function MySitesSection({ navigate, isPaid = false, limit = null, onSell = null, onViewAll = null, title = 'Recent apps' }) {
  const [sites, setSites] = useState(null);   // null = loading
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    authFetch('/api/websites')
      .then(({ websites }) => {
        if (!cancelled) setSites(Array.isArray(websites) ? websites : []);
      })
      .catch((e) => { if (!cancelled) { setError(e.message); setSites([]); } });
    return () => { cancelled = true; };
  }, []);

  const patch = useCallback(async (id, updates) => {
    // Optimistic, then reconciled with what the server actually stored. A rename
    // that silently failed would otherwise leave the card showing a title that
    // does not exist anywhere.
    setSites((cur) => cur.map((s) => (s.id === id ? { ...s, ...updates } : s)));
    try {
      const { website } = await authFetch(`/api/websites/${id}`, {
        method: 'PATCH', body: JSON.stringify(updates),
      });
      setSites((cur) => cur.map((s) => (s.id === id ? { ...s, ...website } : s)));
    } catch (e) {
      toast.error(e.message || 'Could not save that.');
      // Re-read rather than guess at the previous value.
      authFetch('/api/websites')
        .then(({ websites }) => setSites(Array.isArray(websites) ? websites : []))
        .catch(() => {});
    }
  }, []);

  const onEdit = (app) => navigate(`/build/${app.id}`, { state: { website: app } });

  const handlers = {
    onEdit,
    onPreview: (app) => navigate(`/preview/${app.id}`),
    onGetCode: onEdit,
    onRename: (id, title) => patch(id, { title }),
    onTogglePublic: (site) => patch(site.id, { is_public: !site.is_public }),
    onCopyLink: (site) => {
      navigator.clipboard.writeText(`${window.location.origin}/preview/${site.id}`)
        .then(() => toast.success('Share link copied!'))
        .catch(() => toast.error('Could not copy.'));
    },
    onDelete: async (id) => {
      try {
        await authFetch(`/api/websites/${id}`, { method: 'DELETE' });
        setSites((cur) => cur.filter((s) => s.id !== id));
        toast.success('Site deleted.');
      } catch (e) { toast.error(e.message || 'Failed to delete.'); }
    },
    onExportZip: async (site) => {
      if (!isPaid && !site.purchased_at) {
        toast.error('Buy this site or upgrade your plan to export the code.');
        return;
      }
      try {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        buildSiteFiles(site).forEach((f) => zip.file(f.name, f.content));
        const blob = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(site.title || 'website').replace(/\s+/g, '-')}.zip`;
        a.click();
        URL.revokeObjectURL(url);
      } catch { toast.error('Failed to export ZIP.'); }
    },
  };

  if (sites === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-500 py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your apps…
      </div>
    );
  }

  const shown = limit ? sites.slice(0, limit) : sites;

  return (
    <div className="text-left">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-white font-semibold text-lg">{title}</h2>
        {/* Only where there is more to see than is shown. */}
        {limit && sites.length > 0 && (
          <button
            onClick={() => (onViewAll ? onViewAll() : navigate('/build?view=dashboard&section=sites'))}
            className="flex items-center gap-1 text-sm text-zinc-500 hover:text-white transition-colors"
          >
            View all <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

      {shown.length === 0 ? (
        <div className="border border-dashed border-zinc-800 rounded-2xl p-10 text-center">
          <LayoutGrid className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">Your generated apps will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((site) => (
            <RecentAppCard
              key={site.id}
              site={site}
              // A site bought outright carries its own export rights, so the
              // card unlocks for it even on a free plan.
              isPaid={isPaid || !!site.purchased_at}
              {...handlers}
              onSell={onSell}
            />
          ))}
        </div>
      )}
    </div>
  );
}
